import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Users, Copy, Clock, Send, Loader2, RefreshCw } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { KPICard } from "@/components/nadia/KPICard";
import { NadiaAvatar } from "@/components/nadia/NadiaAvatar";
import { FollowUpCard } from "@/components/nadia/FollowUpCard";
import { LeadRow, LEAD_STATUS } from "@/components/nadia/LeadRow";
import {
  listLeads, setLeadStatus, checkStaleLeads, listFollowUps, approveFollowUp, rejectFollowUp,
} from "@/lib/api";

import { useCreditGate } from "@/components/nadia/CreditGate";

export default function Leads() {
  const { gate, dialog: creditDialog } = useCreditGate();
  const [data, setData] = useState(null);
  const [fus, setFus] = useState({ items: [], mode: "mock" });
  const [filter, setFilter] = useState("");
  const [checking, setChecking] = useState(false);

  const load = useCallback(() => {
    listLeads(filter).then(setData).catch(() => setData({ items: [], summary: {} }));
    listFollowUps("PREPARA").then(setFus).catch(() => {});
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    await setLeadStatus(id, status).catch(() => toast.error("Errore"));
    toast.success(`Lead aggiornato: ${LEAD_STATUS[status]?.label || status}`);
    load();
  };

  const check = async () => {
    setChecking(true);
    try {
      const r = await checkStaleLeads();
      toast.success(r.prepared.length
        ? `Nadia ha preparato ${r.prepared.length} follow-up: approvali qui sotto`
        : "Nessun lead senza risposta oltre la finestra");
      load();
    } catch { toast.error("Errore nella verifica"); }
    finally { setChecking(false); }
  };

  const doApprove = async (id, text, opId) => {
    try {
      const r = await approveFollowUp(id, text, opId);
      toast.success(r.mode === "live" ? "Follow-up inviato su WhatsApp" : "WhatsApp non configurato: messaggio non inviato");
    } catch { toast.error("Invio non riuscito"); }
    load();
  };
  const approve = (id, text) => gate("whatsapp_message", (opId) => doApprove(id, text, opId));
  const reject = async (id) => {
    await rejectFollowUp(id).catch(() => toast.error("Errore"));
    toast("Follow-up annullato, nessun messaggio inviato");
    load();
  };

  if (!data) return <Skeleton className="h-96 bg-white/5" />;
  const s = data.summary || {};

  return (
    <>
      {creditDialog}
      <PageHeader
        eyebrow="Lead"
        title="Chi ti ha scritto"
        subtitle="Nadia riconosce i duplicati per email e telefono, ricorda lo stato di ogni contatto e prepara i follow-up. Tu approvi, lei invia."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="leads-kpis">
        <KPICard label="Lead unici" value={s.total ?? 0} icon={Users} testId="kpi-leads-total" />
        <KPICard label="Duplicati evitati" value={s.duplicates_avoided ?? 0} icon={Copy} testId="kpi-leads-dup" helper="non conteggiati" />
        <KPICard label="Senza risposta" value={s.by_status?.nuovo ?? 0} icon={Clock} testId="kpi-leads-new" helper={`finestra ${s.window_hours ?? 24}h`} />
        <KPICard label="Follow-up inviati" value={s.follow_ups_sent ?? 0} icon={Send} testId="kpi-leads-fu" helper={`${s.follow_ups_pending ?? 0} da approvare`} />
      </div>

      {/* Follow-ups awaiting approval */}
      <div className="nadia-card p-5 sm:p-6 mb-6" data-testid="follow-ups-card">
        <div className="flex items-start gap-4 flex-wrap">
          <NadiaAvatar size="sm" />
          <div className="flex-1 min-w-[220px]">
            <h2 className="font-heading text-lg font-semibold text-slate-100">Auto-rilancio WhatsApp</h2>
            <p className="text-sm text-slate-400 mt-0.5">
              Se un lead non risponde entro {s.window_hours ?? 24} ore, Nadia prepara il messaggio. Lo leggi tu, lo approvi tu.
              Il controllo automatico gira ogni 15 minuti.
            </p>
          </div>
          <Button
            onClick={check}
            disabled={checking}
            variant="outline"
            className="border-white/10 text-slate-200 bg-transparent hover:text-cyan-200 hover:border-cyan-400/50"
            data-testid="check-stale-leads"
          >
            {checking ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />}
            Verifica lead senza risposta
          </Button>
        </div>
        <div className="mt-4 space-y-3">
          {fus.items.length === 0 ? (
            <div className="text-sm text-slate-500 rounded-xl border border-dashed border-white/10 px-4 py-5 text-center" data-testid="follow-ups-empty">
              Nessun follow-up in attesa. Nadia ti avviserà qui quando ne prepara uno.
            </div>
          ) : (
            fus.items.map((fu) => (
              <FollowUpCard key={fu.id} fu={fu} mode={fus.mode} onApprove={approve} onReject={reject} />
            ))
          )}
        </div>
      </div>

      {/* Filter + list */}
      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="lead-filters">
        <FilterChip active={filter === ""} onClick={() => setFilter("")} label="Tutti" id="all" />
        {Object.entries(LEAD_STATUS).map(([k, v]) => (
          <FilterChip key={k} active={filter === k} onClick={() => setFilter(k)} label={v.label} id={k} />
        ))}
      </div>
      <div className="space-y-2" data-testid="leads-list">
        {data.items.length === 0 ? (
          <div className="nadia-card p-8 text-center text-sm text-slate-500" data-testid="leads-empty">
            Ancora nessun lead. Arriveranno dalla tua landing appena l'annuncio parte.
          </div>
        ) : (
          data.items.map((l) => <LeadRow key={l.id} lead={l} onStatus={changeStatus} />)
        )}
      </div>
    </>
  );
}

function FilterChip({ active, onClick, label, id }) {
  return (
    <button
      onClick={onClick}
      data-testid={`lead-filter-${id}`}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
        active ? "bg-cyan-500/10 text-cyan-200 border-cyan-500/30" : "text-slate-400 border-white/10 hover:text-slate-100 hover:border-white/20"
      }`}
    >
      {label}
    </button>
  );
}
